/**
 * Cámara Vol3D: turntable con Z craneocaudal (eje de las cortes).
 * yaw=0, pitch=0 → coronal de frente (cámara en −Y, mira +Y).
 * pitch > 0 → ligeramente desde arriba. El orbit gira alrededor del centro.
 */

export const MPR3D_DEFAULT_YAW = 0.32
export const MPR3D_DEFAULT_PITCH = 0.18
export const MPR3D_DEFAULT_ZOOM = 1.08
export const MPR3D_ZOOM_MIN = 0.35
export const MPR3D_ZOOM_MAX = 4.2
export const MPR3D_PAN_MAX = 1.15

const PI = Math.PI
const TAU = PI * 2

/** Envuelve un ángulo a (−π, π]. */
export function wrapAnglePi(a) {
  let x = Number(a) || 0
  x = ((x + PI) % TAU + TAU) % TAU - PI
  return x
}

/**
 * Índice de grilla 3D (0…n-1) → mundo del ray-march (caja centrada en 0.5).
 */
export function mpr3dGridToWorld(gx, gy, gz, n, box) {
  const n1 = Math.max(1, (n | 0) - 1)
  const bx = box && box.x > 0.05 ? box.x : 1
  const by = box && box.y > 0.05 ? box.y : 1
  const bz = box && box.z > 0.05 ? box.z : 1
  return {
    x: (gx / n1 - 0.5) * bx + 0.5,
    y: (gy / n1 - 0.5) * by + 0.5,
    z: (gz / n1 - 0.5) * bz + 0.5,
  }
}

/**
 * Proyecta un punto mundo a UV de pantalla (0…1). null si está detrás de la cámara.
 */
export function mpr3dProjectToUv(world, opts) {
  if (!world) return null
  const yaw = Number(opts && opts.yaw) || 0
  const pitch = opts && opts.pitch != null ? Number(opts.pitch) : 0.18
  const zoom = Math.max(0.35, Number(opts && opts.zoom) || 1)
  const panX = Number(opts && opts.panX) || 0
  const panY = Number(opts && opts.panY) || 0
  const aspect = Math.max(0.001, Number(opts && opts.aspect) || 1)
  const { fx, fy, fz, rx, ry, rz, ux, uy, uz } = mpr3dViewBasis(
    yaw,
    pitch,
    !!(opts && opts.flipX),
    !!(opts && opts.flipY)
  )
  const dist = 1.85 / zoom
  const dx = world.x - (0.5 - fx * dist)
  const dy = world.y - (0.5 - fy * dist)
  const dz = world.z - (0.5 - fz * dist)
  const lz = dx * fx + dy * fy + dz * fz
  if (!(lz > 0.02)) return null
  const lx = dx * rx + dy * ry + dz * rz
  const ly = dx * ux + dy * uy + dz * uz
  const ndcX = lx / (0.78 * lz)
  const ndcY = ly / (0.78 * lz)
  return {
    x: (ndcX + panX) / aspect + 0.5,
    y: 0.5 - ndcY - panY,
  }
}

/**
 * @returns {{ fx:number, fy:number, fz:number, rx:number, ry:number, rz:number, ux:number, uy:number, uz:number }}
 */
export function mpr3dViewBasis(yaw, pitch, flipX = false, flipY = false) {
  const cy = Math.cos(yaw)
  const sy = Math.sin(yaw)
  const cp = Math.cos(pitch)
  const sp = Math.sin(pitch)
  const fx = -sy * cp
  const fy = cy * cp
  const fz = -sp
  // Right según yaw, sin el signo de cos(pitch): si no, al pasar por S/I
  // el eje derecho se invierte y la nariz salta de un borde al otro.
  let rx = cy
  let ry = sy
  let rz = 0
  const rlen = Math.hypot(rx, ry, rz) || 1
  rx /= rlen
  ry /= rlen
  rz /= rlen
  let ux = ry * fz - rz * fy
  let uy = rz * fx - rx * fz
  let uz = rx * fy - ry * fx
  const ulen = Math.hypot(ux, uy, uz) || 1
  ux /= ulen
  uy /= ulen
  uz /= ulen
  if (flipX) {
    rx = -rx
    ry = -ry
    rz = -rz
  }
  if (flipY) {
    ux = -ux
    uy = -uy
    uz = -uz
  }
  return { fx, fy, fz, rx, ry, rz, ux, uy, uz }
}
